/**
 * CopilotDrawer — painel lateral do copiloto Azim AI (TASK-21).
 *
 * Acessibilidade (RNF 9.3, WCAG 2.1 AA):
 * - role="dialog" aria-modal="true" aria-label (Req 9).
 * - Foco gerenciado: ao abrir, foca o composer; Esc fecha e restaura foco ao trigger.
 * - Trap de foco dentro do drawer enquanto aberto.
 * - Contorno de foco visível (CSS :focus-visible).
 *
 * Banner de aviso (Req 9.5):
 * - Sempre visível enquanto o drawer estiver aberto.
 * - Informa que respostas do Azim AI podem ser imprecisas.
 *
 * Degradação graciosa (Req 9.4):
 * - 503 → estado `degraded`: mensagem "Azim AI temporariamente indisponível".
 * - Nenhuma resposta mock é exibida em `degraded`.
 * - Reentrada apenas por botão "Tentar novamente" (ação explícita RETRY).
 *
 * State machine: delegada a useCopilotChat / copilotReducer (design.md §20.15).
 *
 * Referências: design.md §20.15, Req 9, Req 9.1–9.6, RNF 9.3, WEB-ERR-009/010, TASK-21.
 */

import { useEffect, useRef, useCallback, type KeyboardEvent } from "react";
import { Icon } from "@azim-crm/icons";
import { useCopilotChat } from "./useCopilotChat.js";
import { CopilotMessageList } from "./CopilotMessageList.js";
import { CopilotComposer } from "./CopilotComposer.js";
import { CopilotSuggestions } from "./CopilotSuggestions.js";
import styles from "./Copilot.module.css";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CopilotDrawerProps {
  /** Chamado quando o drawer deve ser fechado (Esc, botão fechar). */
  onClose(): void;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

/**
 * Drawer lateral acessível do copiloto Azim AI.
 *
 * Montado pelo Shell quando ⌘J/Ctrl+J é pressionado (design.md §20.15, Req 9).
 * Ao montar, inicializa o estado da state machine em `open` via dispatch OPEN.
 */
export function CopilotDrawer({ onClose }: CopilotDrawerProps) {
  const { state, dispatch, sendMessage } = useCopilotChat();
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<Element | null>(null);

  const { status, messages, streamContent, errorMessage } = state;

  // ---- Inicializa a state machine quando o drawer monta --------------------
  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    dispatch({ type: "OPEN" });
    // Foca o botão de fechar (ou o drawer) ao abrir (WCAG 2.4.3).
    setTimeout(() => closeButtonRef.current?.focus(), 50);
    return () => {
      // Restaura foco ao trigger anterior ao desmontar.
      if (
        previousFocusRef.current &&
        typeof (previousFocusRef.current as HTMLElement).focus === "function"
      ) {
        (previousFocusRef.current as HTMLElement).focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Esc fecha o drawer (Req 9, RNF 9.3) ---------------------------------
  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        dispatch({ type: "CLOSE" });
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [dispatch, onClose]);

  const handleClose = useCallback(() => {
    dispatch({ type: "CLOSE" });
    onClose();
  }, [dispatch, onClose]);

  const handleSend = useCallback(
    (text: string) => {
      sendMessage(text);
    },
    [sendMessage],
  );

  const handleRetry = useCallback(() => {
    dispatch({ type: "RETRY" });
  }, [dispatch]);

  // ---- Focus trap: Tab/Shift+Tab (Finding F6) --------------------------------
  // Mantém o foco dentro do drawer enquanto aberto (WCAG 2.1 SC 2.1.2).
  const handleDrawerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== "Tab") return;
      const drawer = drawerRef.current;
      if (!drawer) return;
      const focusable = Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), ' +
          'select:not([disabled]), textarea:not([disabled]), ' +
          '[tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.tabIndex >= 0);
      if (focusable.length === 0) { e.preventDefault(); return; }
      const first = focusable.at(0);
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [],
  );

  const isLoading = status === "loading";
  const isResponding = status === "responding";
  const isDegraded = status === "degraded";
  const isError = status === "error";
  const composerDisabled = isLoading || isResponding;
  const hasMessages = messages.length > 0;

  return (
    /* Overlay escuro à esquerda do drawer (clique fecha) */
    <div
      className={styles.drawerOverlay}
      onMouseDown={handleClose}
      aria-hidden="false"
      data-testid="copilot-overlay"
    >
      {/* Painel principal — role="dialog" (WCAG 1.3.1, RNF 9.3) */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Azim AI — Copiloto"
        className={styles.drawer}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={handleDrawerKeyDown}
        data-testid="copilot-drawer"
      >
        {/* ---- Cabeçalho ---------------------------------------------------- */}
        <div className={styles.drawerHeader}>
          <div className={styles.drawerTitle}>
            <Icon name="sparkles" size={16} style={{ color: "var(--fg-accent)" }} />
            <span>Azim AI</span>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className={styles.drawerCloseBtn}
            onClick={handleClose}
            aria-label="Fechar copiloto (Esc)"
            title="Fechar (Esc)"
          >
            <Icon name="x" size={16} />
          </button>
        </div>

        {/* ---- Banner de aviso de imprecisão (Req 9.5 — SEMPRE visível) ------- */}
        <div
          className={styles.drawerBanner}
          role="note"
          aria-label="Aviso sobre o Azim AI"
          data-testid="accuracy-banner"
        >
          <Icon name="helpCircle" size={13} aria-hidden="true" />
          <span>
            O Azim AI pode cometer erros. Verifique informações importantes
            antes de tomar decisões.
          </span>
        </div>

        {/* ---- Corpo principal ----------------------------------------------- */}
        <div className={styles.drawerBody}>
          {/* Estado degraded (Req 9.4): 503 = provedor IA indisponível */}
          {isDegraded && (
            <div
              className={styles.degradedState}
              role="alert"
              aria-live="assertive"
              data-testid="degraded-notice"
            >
              <Icon name="alertTriangle" size={24} aria-hidden="true" />
              <p className={styles.degradedTitle}>
                Azim AI temporariamente indisponível
              </p>
              <p className={styles.degradedDesc}>
                O serviço de IA está sendo configurado. Tente novamente em
                instantes.
              </p>
              {/* Reentrada EXPLÍCITA do usuário (design.md §20.15 — RETRY, Req 9.4) */}
              <button
                type="button"
                className={styles.retryButton}
                onClick={handleRetry}
                data-testid="retry-button"
              >
                Tentar novamente
              </button>
            </div>
          )}

          {/* Estado error: 4xx/5xx recuperável */}
          {isError && (
            <div
              className={styles.errorState}
              role="alert"
              aria-live="assertive"
              data-testid="error-notice"
            >
              <p className={styles.errorTitle}>Algo deu errado</p>
              {errorMessage && (
                <p className={styles.errorDesc}>{errorMessage}</p>
              )}
              <button
                type="button"
                className={styles.retryButton}
                onClick={handleRetry}
                data-testid="error-retry-button"
              >
                Tentar novamente
              </button>
            </div>
          )}

          {/* Mensagens + streaming */}
          {!isDegraded && !isError && (
            <>
              {!hasMessages && !isLoading && (
                /* Estado vazio: sugestões iniciais (Req 9.2) */
                <CopilotSuggestions
                  onSelect={(s) => handleSend(s)}
                />
              )}
              {(hasMessages || isLoading || isResponding) && (
                <CopilotMessageList
                  messages={messages}
                  streamContent={streamContent}
                  isLoading={isLoading}
                />
              )}
            </>
          )}
        </div>

        {/* ---- Composer (desabilitado em loading/responding/degraded) --------- */}
        <div className={styles.drawerFooter}>
          <CopilotComposer
            onSend={handleSend}
            disabled={composerDisabled || isDegraded || isError}
          />
          <p className={styles.composerHint} aria-hidden="true">
            Enter para enviar · Shift+Enter para nova linha
          </p>
        </div>
      </div>
    </div>
  );
}
